import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Palette, Switch } from "@eweadn-qiankun/shared-components";
import type { CommunityPreviewDeviceMeta } from "@eweadn-qiankun/shared-types";
import {
  getSupportedDevicesSync,
  resolveCatalogRowFromSnapshot,
} from "@eweadn-qiankun/shared-utils";
import { JiangmengDpiCard } from "../../../../../mouse-management/src/pages/dpiSetting/jiangmeng/JiangmengDpiCard";
import { ZhanliDpiCard } from "../../../../../mouse-management/src/pages/dpiSetting/zhanli/ZhanliDpiCard";
import { colorToHex } from "../../../../../mouse-management/src/pages/dpiSetting/types";
import { resolveMaxDpiBySensor } from "../../../../../mouse-management/src/domain/sensorRules";
import type { MouseSolutionProvider } from "../../../../../mouse-management/src/domain/mouseIdentity";
import {
  getMouseDpiProfiles,
  resolveLegacyDpi,
} from "../utils/mouseConfigPreviewUtils";
import "../../../../../mouse-management/src/pages/dpiSetting/index.scss";
import "../../../../../mouse-management/src/pages/dpiSetting/zhanli/index.scss";
import "../../../../../mouse-management/src/pages/dpiSetting/DpiCard.scss";

interface Props {
  meta: CommunityPreviewDeviceMeta;
  configData?: Record<string, unknown>;
}

function resolvePreviewSensor(meta: CommunityPreviewDeviceMeta): string {
  const catalog =
    resolveCatalogRowFromSnapshot(meta.deviceCatalog ?? null, "mouse") ??
    (getSupportedDevicesSync().mouse as Array<Record<string, unknown>>).find(
      (row) => String(row.deviceId ?? "") === meta.mouseDeviceId
    );
  const fromCatalog = String(catalog?.sensor ?? "").trim();
  if (fromCatalog) return fromCatalog;
  // 展立缺 catalog 时勿落到 3950→42000
  return meta.solutionProvider === "zhanli" ? "3955A" : "3950";
}

function positionColorPopup(
  trigger: HTMLElement,
  popup: HTMLElement
): { left: number; top: number } {
  const tr = trigger.getBoundingClientRect();
  const pr = popup.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gap = 8;
  let left = 0;
  let top = 0;

  const spaceRight = vw - tr.right;
  const spaceBottom = vh - tr.bottom;
  const spaceLeft = tr.left;
  const spaceTop = tr.top;

  if (spaceRight >= pr.width + gap) {
    left = tr.right + gap;
  } else if (spaceLeft >= pr.width + gap) {
    left = tr.left - pr.width - gap;
  } else {
    left = tr.left + tr.width / 2 - pr.width / 2;
  }

  if (spaceBottom >= pr.height + gap) {
    top = tr.bottom + gap;
  } else if (spaceTop >= pr.height + gap) {
    top = tr.top - pr.height - gap;
  } else {
    top = tr.top + tr.height / 2 - pr.height / 2;
  }

  left = Math.max(gap, Math.min(left, vw - pr.width - gap));
  top = Math.max(gap, Math.min(top, vh - pr.height - gap));

  return { left, top };
}

export default function MouseDpiSection({ meta, configData }: Props) {
  const { t } = useTranslation();
  const colorRefs = useRef<(HTMLElement | null)[]>([]);
  const popupRef = useRef<HTMLDivElement>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState<number | null>(null);
  const [popupPos, setPopupPos] = useState({ left: 0, top: 0 });
  const [popupReady, setPopupReady] = useState(false);
  const isZhanli = meta.solutionProvider === "zhanli";
  const maxDpiStages = isZhanli ? 6 : 8;

  const sensor = useMemo(() => resolvePreviewSensor(meta), [meta]);
  const provider = (meta.solutionProvider ||
    "jiangmeng") as MouseSolutionProvider;
  /** 与主页 useSensorSetting → resolveMaxDpiBySensor 对齐 */
  const maxDpiValue = useMemo(
    () => resolveMaxDpiBySensor(sensor, provider, meta.mouseDeviceId),
    [sensor, provider, meta.mouseDeviceId]
  );
  const minDpiValue =
    (isZhanli && sensor === "3955A") || (!isZhanli && sensor === "3955")
      ? 1
      : 50;

  const { profiles, activeIndex: configActiveIndex } = useMemo(() => {
    const parsed = getMouseDpiProfiles(configData);
    const legacyDpi = resolveLegacyDpi(configData);
    const capped = {
      profiles: parsed.profiles.slice(0, maxDpiStages),
      activeIndex: Math.min(parsed.activeIndex, maxDpiStages - 1),
    };
    if (legacyDpi != null && capped.profiles.length > 0) {
      return {
        profiles: capped.profiles.map((p, i) =>
          i === 0 ? { ...p, xValue: legacyDpi, yValue: legacyDpi } : p
        ),
        activeIndex: capped.activeIndex,
      };
    }
    return capped;
  }, [configData, maxDpiStages]);

  /** 展立：全局 XY 独立（任一档 X≠Y 或显式标记） */
  const zhanliXyIndependent = useMemo(() => {
    if (!isZhanli) return false;
    return profiles.some(
      (p) => p.xyIndependent || Number(p.xValue) !== Number(p.yValue)
    );
  }, [isZhanli, profiles]);

  const toggleColorPicker = useCallback((idx: number) => {
    setColorPickerOpen((prev) => (prev === idx ? null : idx));
    setPopupReady(false);
  }, []);

  const updatePopupPosition = useCallback(() => {
    if (colorPickerOpen === null) return;
    const trigger = colorRefs.current[colorPickerOpen];
    const popup = popupRef.current;
    if (!trigger || !popup) return;

    const next = positionColorPopup(trigger, popup);
    setPopupPos(next);
    setPopupReady(true);
  }, [colorPickerOpen]);

  useEffect(() => {
    if (colorPickerOpen === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      const trigger = colorRefs.current[colorPickerOpen];
      const popup = popupRef.current;
      if (
        trigger &&
        !trigger.contains(e.target as Node) &&
        popup &&
        !popup.contains(e.target as Node)
      ) {
        setColorPickerOpen(null);
        setPopupReady(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [colorPickerOpen]);

  useEffect(() => {
    if (colorPickerOpen === null) return;

    const run = () => updatePopupPosition();
    run();
    const rafId = requestAnimationFrame(run);
    window.addEventListener("resize", run);
    window.addEventListener("scroll", run, true);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", run);
      window.removeEventListener("scroll", run, true);
    };
  }, [colorPickerOpen, updatePopupPosition]);

  const colorPopup =
    colorPickerOpen !== null && profiles[colorPickerOpen]
      ? createPortal(
          <div
            ref={popupRef}
            className="dpi-color-popup community-mouse-dpi-color-popup"
            style={{
              left: popupPos.left,
              top: popupPos.top,
              visibility: popupReady ? "visible" : "hidden",
            }}
          >
            <Palette
              key={colorPickerOpen}
              value={colorToHex(profiles[colorPickerOpen].color)}
            />
          </div>,
          document.body
        )
      : null;

  return (
    <div className="community-mouse-dpi-preview">
      <div className="dpi-setting-page">
        <div className="dpi-header">
          <div className="header-text">
            <h1>{t("mouse.dpi.title")}</h1>
            <span className="dpi-count">
              {profiles.length}/{maxDpiStages}
            </span>
            {isZhanli ? <p>{t("mouse.dpi.description")}</p> : null}
          </div>
          {isZhanli ? (
            <div className="dpi-header-actions">
              <div className="xy-independent-toggle">
                <span className="xy-independent-label">
                  {t("mouse.dpi.xyIndependent")}
                </span>
                <Switch
                  checked={zhanliXyIndependent}
                  onChange={() => undefined}
                />
              </div>
            </div>
          ) : null}
        </div>

        <div className="dpi-grid">
          {profiles.map((profile, index) =>
            isZhanli ? (
              <ZhanliDpiCard
                key={index}
                profile={{ ...profile, xyIndependent: zhanliXyIndependent }}
                index={index}
                isActive={index === configActiveIndex}
                selectable={false}
                canDelete={profiles.length > 1}
                maxDpiValue={maxDpiValue}
                minDpiValue={minDpiValue}
                onSelect={() => undefined}
                onXValueChange={() => undefined}
                onXValueChangeComplete={() => undefined}
                onYValueChange={() => undefined}
                onYValueChangeComplete={() => undefined}
                onDelete={() => undefined}
                onColorClick={toggleColorPicker}
                colorButtonRef={(el) => {
                  colorRefs.current[index] = el;
                }}
              />
            ) : (
              <JiangmengDpiCard
                key={index}
                profile={profile}
                index={index}
                isActive={index === configActiveIndex}
                selectable={false}
                canDelete={profiles.length > 1}
                maxDpiValue={maxDpiValue}
                minDpiValue={minDpiValue}
                onSelect={() => undefined}
                onXValueChange={() => undefined}
                onXValueChangeComplete={() => undefined}
                onYValueChange={() => undefined}
                onYValueChangeComplete={() => undefined}
                onToggleXYIndependent={() => undefined}
                onDelete={() => undefined}
                onColorClick={toggleColorPicker}
                colorButtonRef={(el) => {
                  colorRefs.current[index] = el;
                }}
              />
            )
          )}
        </div>

        {colorPopup}
      </div>
    </div>
  );
}
